import React, { FC, useContext } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { ReactReduxContext } from 'react-redux';
import { Saga } from 'redux-saga';

import { AugmentedStore } from 'ia-react-core/~/interfaces/AugmentedStore';
import getWrappedDisplayName from '../../../utils/hocs/getWrappedDisplayName';
import getInjectors from './sagaInjectors';

export interface InjectSagaProps<State> {
  name: string;
  scope: string[];
  store: AugmentedStore<State>;
}

export interface InjectSagaOptions<Props> {
  key: string | null | ((props: Props) => string);
  saga?: Saga;
  sagaFactory?: (props: Props) => Saga;
}

export interface InjectSagaMock {}

const injectSaga = <Props, State extends object = {}>({ key, saga, sagaFactory }: InjectSagaOptions<Props>) => (WrappedComponent: FC<InjectSagaProps<State>>) => {
  class SagaInjector extends React.PureComponent<Props & InjectSagaProps<State>> {
    static WrappedComponent = WrappedComponent;

    sagaKey: string;
    injectors = getInjectors(this.props.store); /* eslint-disable-line react/destructuring-assignment */

    constructor(props: Props & InjectSagaProps<State>) {
      super(props);

      const { injectSaga: injectSagaAction } = this.injectors;

      this.sagaKey = (typeof key === 'function') ? key(props) : key;

      const resolvedSaga = sagaFactory ? sagaFactory(this.props) : saga;
      injectSagaAction(this.sagaKey, { saga: resolvedSaga }, () => this.props);
    }

    componentWillUnmount() {
      const { ejectSaga } = this.injectors;

      ejectSaga(this.sagaKey);
    }

    render() {
      return <WrappedComponent {...this.props} />;
    }
  }

  const InjectSaga = (props: Props & InjectSagaProps<State>) => {
    const { store } = useContext(ReactReduxContext);

    return (
      <SagaInjector store={store} {...props} />
    );
  };

  InjectSaga.displayName = getWrappedDisplayName('InjectSaga', WrappedComponent);

  return hoistNonReactStatics(InjectSaga, WrappedComponent);
};

/**
 * @param {string} key A key of the saga.
 * @param {function} saga A root saga that will be injected.
 * @param {function} sagaFactory A function returing the saga.
 *
 * @since 0.4.0
 * @see See [Redux-Saga](https://redux-saga.js.org/)
 * @visibleName injectSaga
 * @component
 */
export default injectSaga;
